import React from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Divider, List, ListItem, ListItemText,
  Box, Typography, Chip, CircularProgress, Paper, Alert
} from '@mui/material';

const JobDetailDialog = ({ 
  open, 
  onClose, 
  job, 
  loading 
}) => {
  
  // Format JSON for display
  const formatJSON = (obj) => {
    if (!obj) return 'null';
    return JSON.stringify(obj, null, 2);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Job Details
          </Typography>
          {job?.status && (
            <Chip
              label={job.status}
              color={
                job.status === 'completed' ? 'success' :
                job.status === 'failed' ? 'error' :
                job.status === 'processing' ? 'primary' : 'default'
              }
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : job ? (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Basic Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Job ID" secondary={job.id} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Type" secondary={job.type} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Progress" secondary={`${job.progressPercent}%`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Created" secondary={formatDate(job.createdAt)} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Updated" secondary={formatDate(job.updatedAt)} />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Entity ID" 
                  secondary={
                    <Link 
                      to={`/books/${job.entityId}`}
                      style={{ color: 'primary.main' }}
                    >
                      {job.entityId}
                    </Link>
                  } 
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Parameters
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2, overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.85rem' }}>
                {formatJSON(job.params)}
              </pre>
            </Paper>
            
            {job.errorMessage && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" color="error" gutterBottom>
                  Error Message
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'error.50', 
                    mb: 2, 
                    overflow: 'auto',
                    maxHeight: '200px' // Limit height for very long messages
                  }}
                >
                  <pre 
                    style={{ 
                      margin: 0, 
                      fontSize: '0.85rem', 
                      color: 'error.main',
                      whiteSpace: 'pre-wrap', // Allow text to wrap
                      wordBreak: 'break-word' // Break long words if needed
                    }}
                  >
                    {job.errorMessage}
                  </pre>
                </Paper>
              </>
            )}
            
            {job.result && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Result
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.85rem' }}>
                    {formatJSON(job.result)}
                  </pre>
                </Paper>
              </>
            )}
          </Box>
        ) : (
          <Alert severity="warning">No job details available</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {job?.status === 'failed' && (
          <Button 
            variant="contained" 
            color="primary"
            // This would be implemented if you have a retry functionality
            // onClick={() => onRetry(job.id)}
          >
            Retry Job
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailDialog;
